import { lockPageScroll } from '@udemy/design-system-utils';
import { isChecked } from '@udemy/react-checked-state-components';
import classNames from 'classnames';
import React from 'react';
import { SideDrawerRadio } from '../side-drawer-radio/side-drawer-radio.react-component';
import styles from "../side-drawer/side-drawer.module.css";

/**
 * The SubDrawer component.
 *
 * @remarks
 * Renders a sub-drawer inside {@link SideDrawer}.
 * Precise DOM structure is required if you have sub-drawers at level N > 1.
 * You must order the sub-drawers at level N right before their parent drawer at level N - 1,
 * and group them inside a div.
 *
 * **Example:**
 *
 * A
 * - B
 *   - C
 *   - D
 * - E
 *   - F
 *   - G
 *
 * In the above diagram, the main level is A. The drawers at level 1 are B and E.
 * The drawers at level 2 are C, D, F, and G. You must order the drawers like so:
 * ```
 * <SideDrawer
 *     mainDrawerId="A"
 *     subDrawers={
 *         <>
 *             <div>
 *                 <SubDrawer id="C" level={2} />
 *                 <SubDrawer id="D" level={2} />
 *                 <SubDrawer id="B" level={1} />
 *             </div>
 *             <div>
 *                 <SubDrawer id="F" level={2} />
 *                 <SubDrawer id="G" level={2} />
 *                 <SubDrawer id="E" level={1} />
 *             </div>
 *         </>
 *     }
 * />
 * ```
 *
 * Note that C and D are right before their parent B. F and G are right before their parent E.
 * The reason this is required is that we use a CSS selector that targets the parent sub-drawer,
 * and this selector relies on `:last-of-type`, `+`, and `~`, all of which are order-sensitive.
 */
export const SubDrawer = _ref => {
  let {
    className,
    children,
    id,
    level,
    onSelect,
    style,
    ...props
  } = _ref;
  const drawerRef = /*#__PURE__*/React.createRef();

  const onChange = event => {
    if (isChecked(event)) {
      onSelect === null || onSelect === void 0 ? void 0 : onSelect();
      lockPageScroll(drawerRef.current);
    }
  };

  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(SideDrawerRadio, {
    id: id,
    onChange: onChange
  }), /*#__PURE__*/React.createElement(`div-l${level}`, { ...props,
    class: classNames(className, 'js-drawer', styles['side-drawer']),
    style: { ...style,
      zIndex: level
    }
  }, /*#__PURE__*/React.createElement("div", {
    ref: drawerRef,
    className: styles['drawer-content']
  }, children)));
};
//# sourceMappingURL=sub-drawer.react-component.js.map